import {
  forwardRef,
  ComponentPropsWithoutRef,
  ReactNode,
  type Ref,
  type ElementType,
  type JSX,
  type JSXElementConstructor,
} from "react";
import cx from "classnames";

import styles from "./FieldLabel.module.scss";
import { TooltipProps } from "../Tooltip";
import { SrOnly } from "../SrOnly";
import { FieldLabelMoreInfoIcon } from "./internal/FieldLabelMoreInfoIcon";
import { AiMarkWithTooltipOrPopoverProps } from "../../types";
import { AiMark } from "../../components/AiMark";
import { InlineMarkdown } from "../../internal/components/InlineMarkdown";

type IntrinsicAttributes<
  E extends keyof JSX.IntrinsicElements | JSXElementConstructor<unknown>,
> = JSX.LibraryManagedAttributes<E, ComponentPropsWithoutRef<E>>;

type FieldLabelOwnProps<E extends ElementType = ElementType> =
  AiMarkWithTooltipOrPopoverProps & {
    /** The HTML element to render */
    el?: E;
    /** Unique identifier for the label element */
    id?: string;
    /**
     * Whether the field is required. Shows a red asterisk (*) when true.
     * @default false
     */
    required?: boolean;
    /** Additional information to display in a tooltip */
    moreInfo?: ReactNode;
    /** ID for the sr-only description span, referenced via aria-describedby on the associated input. @internal */
    moreInfoId?: string;
    /**
     * Controls the open state of the more info tooltip.
     * @default false
     * @deprecated The more info tooltip is now triggered by a keyboard-accessible button. This prop has no effect.
     */
    moreInfoOpen?: TooltipProps["open"];
  };

/**
 * Props for the FieldLabel component.
 */
export type FieldLabelProps<E extends ElementType = ElementType> =
  FieldLabelOwnProps<E> &
    Omit<IntrinsicAttributes<E>, keyof FieldLabelOwnProps>;

/**
 * FieldLabel component for form field labels with optional required indicator and help tooltip.
 *
 * Features:
 * - Accessible form label with proper semantic HTML
 * - Optional required field indicator with red asterisk
 * - Help tooltip with info icon for additional context
 * - Screen reader support for required field announcement
 * - Keyboard-accessible info icon button for additional field context
 * - Flexible content support for label text and help information
 * - Proper ARIA attributes and semantic structure
 * - Responsive design with consistent typography
 * - Polymorphic element support for different HTML elements
 *
 * @example
 * <FieldLabel htmlFor="email" required>
 *   Email Address
 * </FieldLabel>
 * <input id="email" type="email" />
 *
 * @example
 * <FieldLabel
 *   el="label"
 *   htmlFor="password"
 *   required
 *   moreInfo="Password must be at least 8 characters long"
 * >
 *   Password
 * </FieldLabel>
 * <input id="password" type="password" />
 *
 * @example
 * <FieldLabel el="legend" required>
 *   Form Section
 * </FieldLabel>
 */
const FieldLabelElement = forwardRef(
  (
    {
      aiMark,
      el: Element = "label",
      children,
      className,
      moreInfo,
      moreInfoId,
      required,
      moreInfoOpen: _moreInfoOpen, // deprecated, ignored
      ...props
    }: FieldLabelProps,
    ref,
  ) => {
    const FieldLabelClassNames = cx([styles["label"]], className);

    const labelText = (
      <span className={styles["label-text"]}>
        {/* TODO(v4.0.0): remove typeof check — children will always be string */}
        {typeof children === "string" ? (
          <InlineMarkdown value={children} />
        ) : (
          children
        )}
        {required ? (
          <>
            <span aria-hidden className={styles["required"]}>
              *
            </span>
            <SrOnly>Required</SrOnly>
          </>
        ) : null}
      </span>
    );

    const hasMoreInfoOrAiMark = moreInfo || aiMark;

    // When el="label" with moreInfo, a <button> cannot be a descendant of <label> per HTML spec.
    // Restructure: outer div wrapper + inner <label display:contents> + button as siblings.
    if (Element === "label" && hasMoreInfoOrAiMark) {
      const { htmlFor, id, ...labelProps } =
        props as ComponentPropsWithoutRef<"label">;
      return (
        <div className={FieldLabelClassNames} ref={ref as Ref<HTMLDivElement>}>
          <div className={styles["top-container"]}>
            <label
              htmlFor={htmlFor}
              id={id}
              data-anv="label"
              {...(labelProps as ComponentPropsWithoutRef<"label">)}
            >
              {labelText}
            </label>
            {moreInfo && (
              <FieldLabelMoreInfoIcon
                moreInfo={moreInfo}
                moreInfoId={moreInfoId}
              />
            )}
            {aiMark && (
              <AiMark
                className={styles["ai-mark-icon"]}
                type="gradient"
                popoverOrTooltipConfig={
                  typeof aiMark === "object" ? aiMark : undefined
                }
              />
            )}
          </div>
        </div>
      );
    }

    return (
      <Element
        className={FieldLabelClassNames}
        data-anv="label"
        ref={ref}
        {...props}
      >
        {hasMoreInfoOrAiMark ? (
          <div className={styles["top-container"]}>
            {labelText}
            {moreInfo && (
              <FieldLabelMoreInfoIcon
                moreInfo={moreInfo}
                moreInfoId={moreInfoId}
              />
            )}
            {aiMark && (
              <AiMark
                className={styles["ai-mark-icon"]}
                type="gradient"
                popoverOrTooltipConfig={
                  typeof aiMark === "object" ? aiMark : undefined
                }
              />
            )}
          </div>
        ) : (
          labelText
        )}
      </Element>
    );
  },
);

FieldLabelElement.displayName = "FieldLabel";

export const FieldLabel = FieldLabelElement as <
  E extends ElementType = "label",
>(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  props: FieldLabelProps<E> & { ref?: React.Ref<any> },
) => JSX.Element;
